import { useContext } from "react";
import { Box, Typography } from "@mui/material";
import { MainContext } from "../../../../main/context/MainContextProvider";
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

export type LossItem = {
    name: string;
    message: string;
    value: number;
    percentage: number;
};

export type TopLossesCardProps = {
    title: string;
    lossType: "avoidable" | "expected";
    losses: LossItem[];
    totalLoss: number;
};

export default function TopLossesCard({ title, lossType, losses, totalLoss }: TopLossesCardProps) {
    const { theme } = useContext(MainContext);

    const isAvoidable = lossType === "avoidable";
    const primaryColor = isAvoidable ? "#10B981" : "#3B82F6";
    const backgroundColor = isAvoidable ? "rgba(16, 185, 129, 0.08)" : "rgba(59, 130, 246, 0.08)";
    const borderColor = isAvoidable ? "rgba(16, 185, 129, 0.3)" : "rgba(59, 130, 246, 0.3)";

    // Pegar top 3
    const topLosses = losses
        .sort((a, b) => b.value - a.value)
        .slice(0, 3);

    const lossPercentage = ((topLosses.reduce((sum, l) => sum + l.value, 0) / totalLoss) * 100) || 0;

    return (
        <Box
            sx={{
                backgroundColor: backgroundColor,
                border: `1px solid ${borderColor}`,
                borderRadius: "8px",
                padding: "16px",
                height: "100%"
            }}
        >
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
                {isAvoidable ? (
                    <TrendingDownIcon sx={{ color: primaryColor, fontSize: 20 }} />
                ) : (
                    <WarningAmberIcon sx={{ color: primaryColor, fontSize: 20 }} />
                )}
                <Typography sx={{ fontSize: "14px", fontWeight: "600", color: primaryColor }}>
                    {title}
                </Typography>
            </Box>

            {/* Top Losses List */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {topLosses.length === 0 ? (
                    <Typography sx={{ fontSize: "12px", color: theme.secondaryTextColor, fontStyle: "italic" }}>
                        Nenhuma perda registrada
                    </Typography>
                ) : (
                    topLosses.map((loss, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "10px 12px",
                                backgroundColor: theme.cardBoxBackgroudColor,
                                borderRadius: "6px",
                                border: `1px solid ${borderColor}`
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
                                <Box
                                    sx={{
                                        minWidth: "24px",
                                        height: "24px",
                                        borderRadius: "50%",
                                        backgroundColor: `${primaryColor}20`,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center"
                                    }}
                                >
                                    <Typography sx={{ fontSize: "12px", fontWeight: "700", color: primaryColor }}>
                                        {index + 1}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                                    <Typography sx={{ fontSize: "13px", fontWeight: "500", color: theme.primaryTextColor }}>
                                        {loss.name}
                                    </Typography>
                                    <Typography sx={{ fontSize: "13px", fontWeight: "300", color: theme.primaryTextColor }}>
                                        {loss.message}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                                <Typography sx={{ fontSize: "14px", fontWeight: "600", color: primaryColor }}>
                                    {loss.value.toFixed(1)} MWh
                                </Typography>
                                <Typography sx={{ fontSize: "11px", color: theme.secondaryTextColor }}>
                                    {loss.percentage.toFixed(1)}% do total
                                </Typography>
                            </Box>
                        </Box>
                    ))
                )}
            </Box>

            {/* Total Summary */}
            {topLosses.length > 0 && (
                <Box
                    sx={{
                        marginTop: "16px",
                        paddingTop: "12px",
                        borderTop: `1px solid ${borderColor}`,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                    }}
                >
                    <Typography sx={{ fontSize: "12px", fontWeight: "500", color: theme.secondaryTextColor }}>
                        Top 3 representa:
                    </Typography>
                    <Typography sx={{ fontSize: "13px", fontWeight: "600", color: primaryColor }}>
                        {lossPercentage > 0 ? lossPercentage.toFixed(1) : 0}% das perdas
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
